import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import './Turnstile.css';

// Cloudflare Turnstile: the check that shows a visitor is human, on the sign-up and log-in forms.
// The widget hands us a one-time token which the API checks with Cloudflare (see
// national_park_explorer/turnstile.py in the API repo). A token works once and expires after five
// minutes, so every submit needs its own: ask for one with the `getFreshToken()` method, which
// resolves when the widget has a token to give (immediately, in the usual case where it solved
// itself in the background).
//
// The site key is public and is baked into the build (see the Dockerfile). With no site key the
// widget renders nothing and `getFreshToken()` gives null, which is how the app runs against a
// local API that has no Turnstile secret configured.

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// How long to wait for a token before giving up. Generous: if Cloudflare decides the visitor needs
// to click something, this is the time they have to do it.
const TOKEN_WAIT_MS = 120000;

export const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

let scriptPromise = null;

function loadScript() {
    if (window.turnstile) return Promise.resolve();
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => {
                scriptPromise = null;              // let a later attempt try again
                reject(new Error('turnstile-script-failed'));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
}

const Turnstile = forwardRef(function Turnstile({ siteKey = SITE_KEY, action }, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const tokenRef = useRef(null);        // a token the widget has given us and nothing has used yet
    const spentRef = useRef(false);       // true once a token has been used: the widget needs a reset
    const waitersRef = useRef([]);        // whoever is waiting for the next token
    const setupFailedRef = useRef(false); // the script or the widget could not be set up at all

    const settle = useCallback((outcome, value) => {
        const waiter = outcome === 'token' ? waitersRef.current.shift() : null;
        if (outcome === 'token') {
            if (waiter) {
                clearTimeout(waiter.timer);
                spentRef.current = true;
                waiter.resolve(value);            // hand it straight over; it is used now
            } else {
                tokenRef.current = value;         // keep it for the next submit
                spentRef.current = false;
            }
            return;
        }
        const waiting = waitersRef.current;
        waitersRef.current = [];
        waiting.forEach((one) => {
            clearTimeout(one.timer);
            one.reject(value);
        });
    }, []);

    useEffect(() => {
        if (!siteKey) return undefined;
        let cancelled = false;

        loadScript()
            .then(() => {
                if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    action,
                    callback: (token) => settle('token', token),
                    'error-callback': () => settle('error', new Error('turnstile-error')),
                    // A token that expires or times out before it is used is no longer any good.
                    'expired-callback': () => { tokenRef.current = null; spentRef.current = true; },
                    'timeout-callback': () => { tokenRef.current = null; spentRef.current = true; },
                });
            })
            .catch((error) => {
                // The script was blocked (an ad-blocker, a network that does not allow it) or the
                // widget would not start. Remember it: without this, a later submit would sit and
                // wait for a token that is never coming.
                if (cancelled) return;
                setupFailedRef.current = true;
                settle('error', error);
            });

        return () => {
            cancelled = true;
            settle('error', new Error('turnstile-unmounted'));
            if (widgetIdRef.current !== null && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
            }
            widgetIdRef.current = null;
            tokenRef.current = null;
            spentRef.current = false;
            setupFailedRef.current = false;
        };
    }, [siteKey, action, settle]);

    useImperativeHandle(ref, () => ({
        /** A token that has not been used yet, for one request. Null when Turnstile is not configured. */
        getFreshToken() {
            if (!siteKey) return Promise.resolve(null);

            if (setupFailedRef.current) {
                return Promise.reject(new Error('turnstile-unavailable'));
            }

            const ready = tokenRef.current;
            if (ready) {
                tokenRef.current = null;
                spentRef.current = true;
                return Promise.resolve(ready);
            }

            return new Promise((resolve, reject) => {
                const waiter = { resolve, reject };
                waiter.timer = setTimeout(() => {
                    waitersRef.current = waitersRef.current.filter((one) => one !== waiter);
                    reject(new Error('turnstile-timeout'));
                }, TOKEN_WAIT_MS);
                waitersRef.current.push(waiter);

                // Only start a new challenge if the last token has been used (or went stale). While the
                // first one is still being worked out there is nothing to restart, and resetting would
                // throw away a challenge the visitor may be part way through.
                if (spentRef.current && widgetIdRef.current !== null && window.turnstile) {
                    spentRef.current = false;
                    window.turnstile.reset(widgetIdRef.current);
                }
            });
        },
    }), [siteKey]);

    if (!siteKey) return null;
    return <div className='turnstile-widget' ref={containerRef} />;
});

export default Turnstile;
